import theme from '@/presentation/assets/styles/theme'
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined'
import useAccount from '@presentation/hooks/auth/use-account'
import { useLDClient } from 'launchdarkly-react-client-sdk'
import { Fragment, useEffect } from 'react'
import { useQuery } from 'react-query'
import { useQueryClient } from 'react-query'
import { i18nTranslationHelper } from '@/i18n'
import { Button, CircularProgress, ListItemButton, Typography } from '@mui/material'
import { Link, useNavigate } from 'react-router-dom'
import { redirectTo, WINDOW_TARGET } from '@presentation/helpers'
import { makeRemoteUserProfileAssessment } from '@/main/factories/usecases/profile-assessment/remote-user-profile-assessment-factory'
import { UserProfileAssessment } from '@/domain/usecases/profile-assessment/user-profile-assessment'
import { UserProfileAssessmentModel } from '@/domain/models/profile-assessment/user-profile-assessment-model'
import { Analytics } from '@presentation/pages/profile-assessment/analytics'
import { ValidAccountState } from '@presentation/contexts/auth'
import PathConstants from '@/main/routes/path-contants'
import { FeatureFlag } from '@/infra/feature-flag/feature-flag'

type mode = 'desktop' | 'mobile' | 'flat'

export default function ProfileAssessmentButton({ mode = 'desktop' }: { mode?: mode }) {
  const remote = makeRemoteUserProfileAssessment(useQueryClient())
  const [account, setAccount] = useAccount() as ValidAccountState
  const t = i18nTranslationHelper.getTComponents
  const navigate = useNavigate()
  const userSpaceEnabled = FeatureFlag.Key.USER_SPACE_ENABLED.isEnabled(useLDClient())

  const { latestProfileAssessment } = account
  const { data, isFetching, remove } = useQuery({
    queryKey: UserProfileAssessment.KeyCreate,
    queryFn: () => remote.create(),
    enabled: false,
    retry: 2
  })

  useEffect(() => {
    if (data?.url) {
      setAccount({
        ...account,
        latestProfileAssessment: data
      })
      openUrl(data)
    }
    remove()
  }, [data])

  const handleClick = async () => {
    if (latestProfileAssessment?.url) {
      openUrl(latestProfileAssessment)
    } else {
      navigate(PathConstants.Routes.PROFILE_ASSESSMENT_GET_STARTED.value)
    }
  }

  const openUrl = (data: UserProfileAssessmentModel) => {
    Analytics.trackOpenProfileAssessment(data)
    if (mode !== 'flat' && latestProfileAssessment?.finished && userSpaceEnabled) {
      navigate(PathConstants.Routes.USER_SPACE.value)
    } else {
      redirectTo(data.url, WINDOW_TARGET.BLANK)
      navigate(PathConstants.Routes.PROFILE_ASSESSMENT_IN_PROGRESS.value)
    }
  }

  return (
    <Fragment>
      {mode == 'mobile' && (
        <ListItemButton
          component={Link}
          sx={{ mt: theme.spacing(5 / 8), mb: theme.spacing(5 / 8), flexGrow: 0 }}
          onClick={handleClick}
          to="#"
        >
          {isFetching ? (
            <CircularProgress size={theme.spacing(3)} sx={{ color: theme.palette.grey[900] }} />
          ) : (
            <SchoolOutlinedIcon sx={{ color: theme.palette.grey[900] }} />
          )}
          <Typography
            variant="h6"
            color="grey.900"
            sx={{ ml: theme.spacing(3 / 2), width: '100%' }}
          >
            {latestProfileAssessment?.finished
              ? t('profileAssessmentButton.testResult')
              : t('profileAssessmentButton.profileTest')}
          </Typography>
        </ListItemButton>
      )}
      {mode == 'desktop' && (
        <Button
          startIcon={
            isFetching ? (
              <CircularProgress size={theme.spacing(2)} color="success" />
            ) : (
              <SchoolOutlinedIcon fontSize="small" />
            )
          }
          variant="outlined"
          onClick={handleClick}
          disabled={isFetching}
          sx={{
            backgroundColor: latestProfileAssessment?.finished
              ? 'var(--primary-50)'
              : 'var(--success-50)',
            fontSize: theme.spacing(1.75),
            color: latestProfileAssessment?.finished
              ? 'var(--primary-dark)'
              : 'var(--success-dark)',
            borderRadius: 50,
            borderWidth: 0,
            ml: theme.spacing(1),
            px: theme.spacing(2),
            py: theme.spacing(1),
            '&:hover': {
              bgcolor: latestProfileAssessment?.finished
                ? 'var(--primary-100)'
                : 'var(--success-100)',
              borderWidth: 0
            }
          }}
        >
          {latestProfileAssessment?.finished
            ? t('profileAssessmentButton.testResult')
            : t('profileAssessmentButton.profileTest')}
        </Button>
      )}
      {mode == 'flat' && (
        <Button
          onClick={handleClick}
          disabled={isFetching}
          color="primary"
          variant="contained"
          sx={{
            borderWidth: 0,
            fontSize: theme.spacing(2),
            px: theme.spacing(4),
            py: theme.spacing(2)
          }}
        >
          {latestProfileAssessment?.finished
            ? t('profileAssessmentButton.testResult')
            : t('profileAssessmentButton.goToTest')}
        </Button>
      )}
    </Fragment>
  )
}
